import {
  combineReducers,
  configureStore,
  UnknownAction,
} from '@reduxjs/toolkit';
import { courier } from './slice/onboardingSlice';
import {
  createTransform,
  persistReducer,
  persistStore,
  PURGE,
} from 'redux-persist';
import type { WebStorage } from 'redux-persist/es/types';
import authReducer from './slice/authSlice';
import menuReducer from './slice/menuSlice';
import { productTypeReducer } from './slice/ProductTypeSlice';
import { productReducer } from './slice/ProductSlice';
import { teamReducer } from './slice/teamsSlice';
import { settingsUsersReducer } from './slice/SettingsUsersSlice';
import { shipmentFilterReducer } from './slice/filterSlice';
import { settingsLocationsReducer } from './slice/SettingsLocationSlice'; // to pass props globly
import languageReducer from './slice/languageSlice';
import appearanceReducer from './slice/appearanceSlice';
import { workflowTemplatesReducer } from './slice/workflowTemplatesSlice';
import shipmentNavigationReducer from './slice/shipmentNavigationSlice';
import { shipmentTabReducer } from './slice/shipmentTabSlice';
import { resetTabs, tabReducer, type TabState } from './slice/tabSlice';
import previewReducer from './slice/previewSlice';
import activityBeaconReducer from './slice/activityBeaconSlice';
import { isPreviewTabActive } from '@/common/helper/tokenStorage';

const combinedReducers = combineReducers({
  Couriers: courier,
  auth: authReducer,
  ProductTypes: productTypeReducer,
  Products: productReducer,
  menu: menuReducer,
  teams: teamReducer,
  settingsUsers: settingsUsersReducer,
  shipmentFilter: shipmentFilterReducer,
  settingsLocations: settingsLocationsReducer,
  language: languageReducer,
  appearance: appearanceReducer,
  workflowTemplates: workflowTemplatesReducer,
  shipmentNavigation: shipmentNavigationReducer,
  shipmentTab: shipmentTabReducer,
  tabReducer: tabReducer,
  preview: previewReducer,
  activityBeacon: activityBeaconReducer,
});

export type RootState = ReturnType<typeof combinedReducers>;

export const RESET_STATE = 'store/RESET_STATE';
export const resetState = () => ({ type: RESET_STATE });

const rootReducer = (
  state: RootState | undefined,
  action: UnknownAction
): RootState => {
  if (action.type === PURGE || action.type === RESET_STATE) {
    state = undefined; // Reset state on PURGE or RESET_STATE
  }
  return combinedReducers(state, action);
};

export function createNoopStorage(): WebStorage {
  return {
    getItem: () => Promise.resolve(null),
    setItem: () => Promise.resolve(),
    removeItem: () => Promise.resolve(),
  };
}

/**
 * Resolves the browser Storage engine that redux-persist should read/write
 * through *at the time of each call*. While this tab has an active preview
 * session ("View As User" / role preview), redux-persist state is routed to
 * `sessionStorage` -- tab-scoped and never inherited by a new tab -- instead
 * of `localStorage`, so previewing never overwrites the admin's real,
 * cross-tab persisted state (menu, teams, saved filters/tabs, etc). Once the
 * preview session ends, writes automatically resume targeting
 * `localStorage` again.
 */
const memoryStorage: Storage = {
  length: 0,
  clear: () => {},
  getItem: () => null,
  key: () => null,
  removeItem: () => {},
  setItem: () => {},
};

function activePersistStorage(): Storage {
  if (typeof window === 'undefined') {
    return memoryStorage;
  }
  if (isPreviewTabActive() && window.sessionStorage) {
    return window.sessionStorage;
  }
  return window.localStorage || memoryStorage;
}

export function createReduxPersistStorage(): WebStorage {
  if (typeof window === 'undefined' || !window.localStorage) {
    return createNoopStorage();
  }

  try {
    const testKey = '__zoomlogi_redux_persist_storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
  } catch {
    return createNoopStorage();
  }

  return {
    getItem: (key: string) => {
      try {
        return Promise.resolve(activePersistStorage().getItem(key));
      } catch {
        return Promise.resolve(null);
      }
    },
    setItem: (key: string, item: string) => {
      try {
        activePersistStorage().setItem(key, item);
      } catch {
        // Guard against post-teardown timers or storage errors
      }
      return Promise.resolve();
    },
    removeItem: (key: string) => {
      try {
        activePersistStorage().removeItem(key);
      } catch {
        // Guard against storage errors
      }
      return Promise.resolve();
    },
  };
}

const storage = createReduxPersistStorage();

// AlertFilterDropdown always resets its own UI to "all selected" (= no
// filter) on mount, so persisting alert_states/alert_types would let a
// stale narrower filter from a previous session silently keep filtering
// results while the UI shows everything selected. Strip them on write only
// -- nothing needs to change on read/rehydrate.
const dashboardFilterTransform = createTransform<TabState, TabState>(
  (inboundState) => {
    if (!inboundState.filterQueryRedux) return inboundState;
    const {
      alert_states: _alert_states,
      alert_types: _alert_types,
      ...rest
    } = inboundState.filterQueryRedux;
    return {
      ...inboundState,
      filterQueryRedux: rest as TabState['filterQueryRedux'],
    };
  },
  (outboundState) => outboundState,
  { whitelist: ['tabReducer'] }
);

const persistConfig = {
  key: 'root', // Key for storage (can be whatever you want)
  storage, // Storage method (localStorage in this case)
  transforms: [dashboardFilterTransform],
  // `appearance` is re-derived from the JWT on every mount (useAppearanceSync)
  // and PersistGateCompat renders with `loading={null}` (doesn't block on
  // rehydration), so persisting it created a race: the correct dark-mode
  // dispatch from the JWT could be clobbered moments later when the async
  // REHYDRATE action landed with a stale cached value. The JWT is already
  // the durable source of truth, so this slice doesn't need a second copy.
  blacklist: ['appearance'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer, // Use persistedReducer here

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Disable serializability checks
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;

export function resetDashboardFilterStore(): void {
  store.dispatch(resetTabs());
}

/**
 * Completely purges and resets both in-memory Redux state and persisted storage.
 * Used during session switching and logout to prevent state leaks across accounts.
 *
 * `purgePersisted` defaults to `true` (full logout/login identity switch).
 * Preview transitions (View As User / Exit Preview) pass `false`: preview
 * sessions never touch `redux-persist`'s localStorage-backed state, so
 * purging it on every preview enter/exit would needlessly wipe UI state
 * (e.g. saved tab/filter preferences) shared with the admin's other tabs.
 */
export async function clearAllStoreState({
  purgePersisted = true,
}: { purgePersisted?: boolean } = {}): Promise<void> {
  store.dispatch(resetState());
  store.dispatch(resetTabs());
  if (!purgePersisted) return;
  await persistor.flush();
  await persistor.purge();
  if (typeof window !== 'undefined') {
    activePersistStorage().removeItem('persist:root');
    try {
      window.localStorage?.removeItem('persist:root');
    } catch {
      // Guard
    }
    try {
      window.sessionStorage?.removeItem('persist:root');
    } catch {
      // Guard
    }
  }
}

export { store, persistor, resetTabs };
